import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { authService, userProfileService, supabase } from '../lib/supabase'; // 引入 supabase
import { UserProfile } from '../types/invitation';
import { createLogger } from '../lib/logger';

const log = createLogger('auth');

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: any }>;
  signUp: (email: string, password: string, userData?: { displayName?: string; phone?: string }) => Promise<{ user: User | null; error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUserProfile = useCallback(async (userId: string) => {
    log.debug('loadUserProfile - 開始載入:', userId);
    try {
      const profile = await userProfileService.getProfile(userId);
      if (profile) {
        log.info('成功載入 profile');
        setUserProfile(profile);
      } else {
        setUserProfile({
          id: 'default',
          userId,
          displayName: user?.email?.split('@')[0] || '用戶',
          phone: '',
          weddingDate: '',
          partnerName: '',
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
    } catch (error) {
      log.error('loadUserProfile 錯誤:', error);
      setUserProfile({
        id: 'default',
        userId,
        displayName: user?.email?.split('@')[0] || '用戶',
        phone: '',
        weddingDate: '',
        partnerName: '',
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
  }, [user?.email]);

  useEffect(() => {
    let isInitialized = false;

    const handleAuthStateChange = async (event: string, currentSession: Session | null) => {
      // 跳過初始化期間的 INITIAL_SESSION 事件，避免重複載入
      if (event === 'INITIAL_SESSION' && !isInitialized) {
        log.debug('跳過初始 INITIAL_SESSION 事件');
        return;
      }

      log.debug('handleAuthStateChange - 事件:', event);

      try {
        setSession(currentSession);
        setUser(currentSession?.user || null);

        if (currentSession?.user) {
          // 暫時跳過 profile 載入，直接創建預設 profile
          const defaultProfile: UserProfile = {
            id: 'default',
            userId: currentSession.user.id,
            displayName: currentSession.user.email?.split('@')[0] || '用戶',
            phone: '',
            weddingDate: '',
            partnerName: '',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          setUserProfile(defaultProfile);
          // await loadUserProfile(currentSession.user.id);
        } else {
          setUserProfile(null);
        }

        setLoading(false);
      } catch (error) {
        log.error('handleAuthStateChange 錯誤:', error);
        setLoading(false);
      }
    };

    // 初始化認證狀態
    const initializeAuth = async () => {
      try {
        log.debug('初始化認證狀態');
        const { data: { session } } = await supabase.auth.getSession();
        
        setSession(session);
        setUser(session?.user || null);
        
        if (session?.user) {
          // 暫時跳過 profile 載入，直接創建預設 profile
          const defaultProfile: UserProfile = {
            id: 'default',
            userId: session.user.id,
            displayName: session.user.email?.split('@')[0] || '用戶',
            phone: '',
            weddingDate: '',
            partnerName: '',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          setUserProfile(defaultProfile);
          // await loadUserProfile(session.user.id);
        } else {
          setUserProfile(null);
        }
        
        setLoading(false);
        isInitialized = true;
        log.info('認證初始化完成');
      } catch (error) {
        log.error('初始化錯誤:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    // 監聽後續的認證狀態變化
    const { data: { subscription } = { subscription: undefined } } = authService.onAuthStateChange(handleAuthStateChange);

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // 登入
  const signIn = async (email: string, password: string) => {
    log.debug('signIn - 設定 loading 為 true');
    setLoading(true);
    try {
      const result = await authService.signIn(email, password);
      log.debug('signIn - 登入結果:', result);
      
      if (result.user) {
        setUser(result.user);
        await loadUserProfile(result.user.id);
        // 成功時不設置 loading false，由 onAuthStateChange 處理
      } else if (result.error) {
        // 登入失敗時立即重置 loading 狀態
        log.debug('signIn - 登入失敗，重置 loading 狀態');
        setLoading(false);
      }
      
      return result;
    } catch (error) {
      log.error('登入異常:', error);
      // 異常情況也要重置 loading 狀態
      setLoading(false);
      return { user: null, error };
    }
  };

  // 註冊
  const signUp = async (email: string, password: string, userData?: { displayName?: string; phone?: string }) => {
    log.debug('signUp - 設定 loading 為 true');
    setLoading(true);
    try {
      const result = await authService.signUp(email, password, userData);
      log.debug('signUp - 註冊結果:', result);
      
      if (result.user) {
        setUser(result.user);
        // 註冊成功後會自動建立用戶資料檔案
        await loadUserProfile(result.user.id);
        // 成功時不設置 loading false，由 onAuthStateChange 處理
      } else if (result.error) {
        // 註冊失敗時立即重置 loading 狀態
        log.debug('signUp - 註冊失敗，重置 loading 狀態');
        setLoading(false);
      }
      
      return result;
    } catch (error) {
      log.error('註冊異常:', error);
      // 異常情況也要重置 loading 狀態
      setLoading(false);
      return { user: null, error };
    }
  };

  // 登出
  const signOut = async () => {
    log.debug('signOut - 設定 loading 為 true');
    setLoading(true);
    try {
      await authService.signOut();
      setUser(null);
      setSession(null);
      setUserProfile(null);
      log.info('signOut - 用戶已登出');
      setLoading(false);
    } catch (error) {
      log.error('登出失敗:', error);
      setLoading(false);
    }
  };

  // 更新用戶資料檔案
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) {
      throw new Error('必須登入才能更新資料檔案');
    }

    try {
      await userProfileService.updateProfile(user.id, updates);
      await loadUserProfile(user.id); // 重新載入資料檔案
    } catch (error) {
      log.error('更新用戶資料檔案失敗:', error);
      throw error;
    }
  };

  // Google OAuth 登入
  const signInWithGoogle = async () => {
    log.debug('signInWithGoogle - 設定 loading 為 true');
    setLoading(true);
    try {
      const result = await authService.signInWithGoogle();
      log.debug('signInWithGoogle - Google 登入結果:', result);
      // OAuth 成功時不設置 loading false，由 onAuthStateChange 處理
      return result;
    } catch (error) {
      log.error('Google 登入失敗:', error);
      setLoading(false); // OAuth 失敗時設置 loading 為 false
      return { error };
    }
  };

  // 重新載入用戶資料檔案
  const refreshProfile = async () => {
    if (!user) return;
    
    try {
      await loadUserProfile(user.id);
    } catch (error) {
      log.error('重新載入用戶資料檔案失敗:', error);
    }
  };

  const value: AuthContextType = {
    user,
    session,
    userProfile,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    updateProfile,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};